import React from 'react';
import { gs } from '../../styles/globalStyles';

// ─── Button ──────────────────────────────────────────────────────────────────
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  children, variant = 'primary', size = 'md', loading = false,
  fullWidth = false, icon, className = '', disabled, ...props
}) => {
  const variantMap = {
    primary: gs.btnPrimary, secondary: gs.btnSecondary,
    outline: gs.btnOutline, ghost: gs.btnGhost, danger: gs.btnDanger,
  };
  const sizeMap = { sm: 'text-xs py-1.5 px-3', md: '', lg: 'text-base py-3 px-7' };

  return (
    <button
      className={`inline-flex items-center justify-center gap-2 ${variantMap[variant]} ${sizeMap[size]} ${fullWidth ? 'w-full' : ''} ${loading ? 'opacity-70 cursor-not-allowed' : ''} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader size="sm" /> : icon}
      {children}
    </button>
  );
};

// ─── Card ────────────────────────────────────────────────────────────────────
interface CardProps {
  children: React.ReactNode;
  hover?: boolean;
  glow?: boolean;
  className?: string;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({ children, hover, glow, className = '', onClick }) => {
  const base = glow ? gs.cardGlow : hover ? gs.cardHover : gs.card;
  return (
    <div className={`${base} ${className}`} onClick={onClick}>
      {children}
    </div>
  );
};

// ─── Loader ──────────────────────────────────────────────────────────────────
interface LoaderProps { size?: 'sm' | 'md' | 'lg'; color?: string; }

export const Loader: React.FC<LoaderProps> = ({ size = 'md', color = '#86C442' }) => {
  const sizes = { sm: 'w-4 h-4 border-2', md: 'w-8 h-8 border-2', lg: 'w-12 h-12 border-3' };
  return (
    <div
      className={`${sizes[size]} rounded-full animate-spin`}
      style={{ borderColor: `${color}20`, borderTopColor: color }}
    />
  );
};

export const PageLoader: React.FC = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-page gap-4">
    <Loader size="lg" />
    <p className="text-gray-500 text-sm font-mono animate-pulse">Loading...</p>
  </div>
);

// ─── Badge ───────────────────────────────────────────────────────────────────
interface BadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'warning' | 'error' | 'info' | 'gray';
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({ children, variant = 'info', className = '' }) => {
  const map = {
    success: gs.badgeSuccess, warning: gs.badgeWarning,
    error: gs.badgeError, info: gs.badgeInfo, gray: gs.badgeGray,
  };
  return <span className={`${map[variant]} ${className}`}>{children}</span>;
};

export const statusBadge = (status: string): React.ReactNode => {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'gray'> = {
    active: 'success', approved: 'success', completed: 'success', closed: 'gray',
    pending: 'warning', open: 'info',
    suspended: 'error', rejected: 'error', cancelled: 'error',
  };
  return <Badge variant={map[status] || 'gray'}>{status.toUpperCase()}</Badge>;
};
